import styled, { useTheme } from 'styled-components'
import { useState } from 'react'
import { FiArrowUpRight } from 'react-icons/fi'
import { device } from '../../breakpoints'
import { MdOutlineKeyboardArrowRight } from 'react-icons/md'

const months = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

const TimelineElement = ({
  data,
  date,
}: {
  data: TimelineItem[]
  date: number[]
}) => {
  const [itemNumber, setItemNumber] = useState(0)
  const theme = useTheme()
  const getArticleLink = (link: string) => {
    let out = link
    const isInternal = /^\d+$/.test(link)

    if (isInternal) {
      out = `https://www.${theme.tenant.key}.ch/${link}`
    }

    return out
  }

  return (
    <Container empty={data.length > 0 ? false : true}>
      <Date>
        {date[1]}/{date[0]}
      </Date>
      {data && (
        <Body>
          {data
            .slice(itemNumber, itemNumber + 1)
            .map((item: TimelineItem, i: number) => (
              <div key={`item-${i}`}>
                <Item>
                  <ImageContainer>
                    <IllustrationContainer>
                      <IllustrationBackground
                        style={{
                          maskImage: `url(${require(`../../../images/${item.category}.png`)})`,
                          WebkitMaskImage: `url(${require(`../../../images/${item.category}.png`)})`,
                        }}
                      />
                      <Illustration
                        version='1.1'
                        xmlns='http://www.w3.org/2000/svg'
                        xmlnsXlink='http://www.w3.org/1999/xlink'
                        viewBox='0 0 500 500'
                      >
                        <path
                          d='M274.4,28.5l49.8,42.4l65.2,5.2c18.4,1.5,33.1,16.1,34.6,34.6l5.2,65.2l42.4,49.8c12,14.1,12,34.8,0,48.9
	l-42.4,49.8l-5.2,65.2c-1.5,18.4-16.1,33.1-34.6,34.6l-65.2,5.2l-49.8,42.4c-14.1,12-34.8,12-48.9,0l-49.8-42.4l-65.2-5.2
	c-18.4-1.5-33.1-16.1-34.6-34.6l-5.2-65.2l-42.4-49.8c-12-14.1-12-34.8,0-48.9l42.4-49.8l5.2-65.2c1.5-18.4,16.1-33.1,34.6-34.6
	l65.2-5.2l49.8-42.4C239.6,16.5,260.4,16.5,274.4,28.5z'
                        />
                      </Illustration>
                    </IllustrationContainer>{' '}
                  </ImageContainer>
                  {item.text}
                  {item.articles && (
                    <ArticleLink>
                      <a target='_blank' href={getArticleLink(item.articles)}>
                        En lire davantage <FiArrowUpRight />
                      </a>
                    </ArticleLink>
                  )}
                </Item>
                {data.length > 1 && (
                  <MoreButtonContainer>
                    <MoreButton
                      onClick={(e) => {
                        e.stopPropagation()
                        setItemNumber((itemNumber + 1) % data.length)
                      }}
                    >
                      <MdOutlineKeyboardArrowRight />
                    </MoreButton>
                  </MoreButtonContainer>
                )}
              </div>
            ))}
        </Body>
      )}
    </Container>
  )
}

const Container = styled.div<{ empty: boolean }>`
  border-left: 1px solid ${({ theme }) => theme.borderColor};
  padding: 1rem;
  position: relative;
  padding-top: 60px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    max-width: 240px;
    min-width: ${({ empty }) => (empty ? 20 : 240)}px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    max-width: 300px;
    min-width: ${({ empty }) => (empty ? 20 : 300)}px;
  }
`

const Date = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  transform: rotate(270deg) translate(-120%, 0);
  transform-origin: left top;
  font-size: 1rem;
`

const IllustrationContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 60px;
  width: 60px;
  position: relative;
  margin-bottom: 2rem;
`

const IllustrationBackground = styled.div`
  background-color: ${({ theme }) => theme.backgroundColor};
  background-size: cover;
  background-position: center;
  width: 80%;
  height: 80%;
  position: absolute;
  masksize: contain;
  -webkit-mask-size: contain;
  top: 10%;
  left: 10%;
`

const Illustration = styled.svg`
  path {
    fill: ${({ theme }) => theme.invertedBackgroundColor};
  }
`

const Image = styled.img`
  width: 60px;
`

const Item = styled.div`
  padding-bottom: 1rem;
  padding-top: 1rem;

  border-top: 1px solid ${({ theme }) => theme.borderColor};

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`

const Body = styled.div`
  line-height: 1.5;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.5rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.6rem;
  }
`

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
`

const MoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

const MoreButton = styled.div`
  cursor: pointer;
  background-color: ${({ theme }) => theme.invertedBackgroundColor};
  color: ${({ theme }) => theme.invertedFontColor};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;

  position: absolute;
  bottom: 3rem;
  right: 2rem;
`

const ArticleLink = styled.div`
  margin-top: 2rem;

  a {
    color: ${({ theme }) => theme.invertedBackgroundColor};
  }
`

export default TimelineElement
